import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { VariableSizeList as VirtualList } from 'react-window';
import { useComponentWebSocket } from './GlobalSettingUtils';

// Memoized log item component for better performance
const LogItem = React.memo(({ index, style, data }) => {
  const { logs, getLevelColor } = data;
  const log = logs[index];
  
  if (!log) return null;

  return (
    <div style={style}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1,
          py: 0.75,
          borderBottom: '1px solid #f0f0f0',
          fontSize: '0.75rem',
          textAlign: 'left',
          minHeight: 'auto'
        }}
      >
        <Chip
          label={log.level}
          size="small"
          sx={{
            backgroundColor: getLevelColor(log.level),
            color: 'white',
            fontWeight: 'bold',
            minWidth: 50,
            maxWidth: 50,
            height: 20,
            fontSize: '0.65rem',
            flexShrink: 0,
            alignSelf: 'flex-start',
            mt: 0.25,
            '& .MuiChip-label': { px: 0.5 }
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            minWidth: 60,
            maxWidth: 60,
            fontSize: '0.65rem',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            textAlign: 'left',
            alignSelf: 'flex-start',
            mt: 0.25
          }}
        >
          {log.timestamp ? log.timestamp.slice(-8) : ''}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              lineHeight: 1.5,
              wordBreak: 'break-word',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              display: 'block',
              margin: 0,
              padding: 0
            }}
          >
            {log.message}
          </Typography>
        </Box>
      </Box>
    </div>
  );
});

LogItem.displayName = 'LogItem';

const GlobalSettingLogs = forwardRef((props, ref) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [availableLogs, setAvailableLogs] = useState([]);
  const [selectedLogFile, setSelectedLogFile] = useState('all');
  const listRef = React.useRef(null);
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  // WebSocket command handlers
  const commandHandlers = {
    'get_logs_response': (message) => {
      console.log('📥 Received logs response:', message);
      
      // Extract data object - WebSocket sends {command: "...", data: {...}}
      const responseData = message.data || message;
      
      // Backend returns {status: "success", logs: [...], total_logs: N, available_logs: [...]} or {status: "error", message: "...", logs: []}
      if (responseData.status === 'error') {
        console.error('Error fetching logs:', responseData.message);
        setLogs([]);
        setLoading(false);
        return;
      }
      
      // Update available log files for dropdown
      if (responseData.available_logs && Array.isArray(responseData.available_logs)) {
        setAvailableLogs(responseData.available_logs);
      }
      
      // Backend now returns simple string array, convert to objects for frontend use
      const logStrings = responseData.logs || [];
      console.log('📊 Log strings received:', logStrings.length, 'lines');
      
      const processedLogs = logStrings.map((logLine, index) => {
        // Try to extract timestamp - handle both prefixed [SERVICE] and non-prefixed formats
        // Match: "[SERVICE] 2025-11-30 12:32:59" or "2025-11-30 12:32:59"
        const timestampMatch = logLine.match(/(?:\[.*?\]\s+)?(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/);
        const timestamp = timestampMatch ? timestampMatch[1] : '';
        
        // Simple level detection
        let level = 'INFO';
        const lowerLine = logLine.toLowerCase();
        if (lowerLine.includes('error') || lowerLine.includes('exception') || lowerLine.includes('failed')) {
          level = 'ERROR';
        } else if (lowerLine.includes('warning') || lowerLine.includes('warn')) {
          level = 'WARNING';
        } else if (lowerLine.includes('debug')) {
          level = 'DEBUG';
        }
        
        return {
          id: index,
          timestamp: timestamp,
          level: level,
          message: logLine,
          rawLine: logLine
        };
      });
      
      console.log('✅ Processed logs:', processedLogs.length, 'items');
      console.log('📋 First few logs:', processedLogs.slice(0, 3));
      
      setLogs(processedLogs);
      setLoading(false);
      setLastRefresh(new Date());
      
      // Scroll to bottom after logs are loaded
      setTimeout(() => {
        if (listRef.current && processedLogs.length > 0) {
          listRef.current.scrollToItem(processedLogs.length - 1, 'end');
        }
      }, 100);
    }
  };

  const { sendCommand } = useComponentWebSocket(commandHandlers);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    refreshData: fetchLogs,
    isModified: () => false,
    saveChanges: () => Promise.resolve(true)
  }));  // Fetch logs from server
  const fetchLogs = useCallback(() => {
    setLoading(true);
    sendCommand('get_logs', { limit: 1000, log_file: selectedLogFile });
  }, [selectedLogFile, sendCommand]);

  // Memoized filtered logs for better performance
  const filteredLogs = useMemo(() => {
    if (!Array.isArray(logs)) return [];
    
    let filtered = logs;

    if (debouncedSearchTerm) {
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        log.level.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level.toLowerCase() === levelFilter);
    }

    return filtered;
  }, [logs, debouncedSearchTerm, levelFilter]);

  // Download logs as text file
  const downloadLogs = useCallback(() => {
    const logsToDownload = filteredLogs.length > 0 ? filteredLogs : logs;
    if (!Array.isArray(logsToDownload) || logsToDownload.length === 0) {
      return; // Don't download if no logs
    }
    
    const logText = logsToDownload.map(log => 
      `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs_${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [logs, filteredLogs]);

  // Get log level color - memoized
  const getLevelColor = useCallback((level) => {
    switch (level.toLowerCase()) {
      case 'error': return '#f44336';
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      case 'debug': return '#9e9e9e';
      default: return '#757575';
    }
  }, []);
  // Memoized data for virtual list
  const virtualListData = useMemo(() => ({
    logs: filteredLogs,
    getLevelColor
  }), [filteredLogs, getLevelColor]);

  // Calculate item height based on content
  const getItemSize = useCallback((index) => {
    const log = filteredLogs[index];
    if (!log) return 50;
    
    // Base height for chip, timestamp, and padding
    const baseHeight = 44;
    
    // Estimate text height based on message length
    // Assume ~80 characters per line with monospace font
    const messageLines = Math.ceil(log.message.length / 80);
    const textHeight = messageLines * 16; // 16px per line with lineHeight 1.5
    
    return Math.max(baseHeight, baseHeight + textHeight - 16);
  }, [filteredLogs]);
  // Auto-refresh functionality - DISABLED
  // useEffect(() => {
  //   if (autoRefresh) {
  //     autoRefreshInterval.current = setInterval(fetchLogs, 5000); // Refresh every 5 seconds
  //   } else {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   }

  //   return () => {
  //     if (autoRefreshInterval.current) {
  //       clearInterval(autoRefreshInterval.current);
  //     }
  //   };
  // }, [autoRefresh]);
  // Initial load
  useEffect(() => {
    fetchLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Box sx={{ maxWidth: '100%', width: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#5757b0' }}>
        System Logs
      </Typography>
      
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        View and manage application logs. Use filters to find specific log entries.
      </Typography>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Search */}
          <TextField
            label="Search logs"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Log file selector */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Log File</InputLabel>
            <Select
              value={selectedLogFile}
              label="Log File"
              onChange={(e) => {
                setSelectedLogFile(e.target.value);
                // Auto-refresh when changing log file
                setTimeout(() => {
                  setLoading(true);
                  sendCommand('get_logs', { limit: 1000, log_file: e.target.value });
                }, 100);
              }}
            >
              <MenuItem value="all">All Services (Aggregated)</MenuItem>
              {availableLogs.map((logFile) => (
                <MenuItem key={logFile} value={logFile}>
                  {logFile.replace('_output.log', '').replace('_error.log', ' (errors)').replace(/_/g, ' ').toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Level filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Level</InputLabel>
            <Select
              value={levelFilter}
              label="Level"
              onChange={(e) => setLevelFilter(e.target.value)}
            >
              <MenuItem value="all">All Levels</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Tooltip title="Refresh logs">
              <IconButton 
                onClick={fetchLogs}
                disabled={loading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>            <Tooltip title="Download logs">
              <IconButton 
                onClick={downloadLogs}
                disabled={logs.length === 0}
                color="primary"
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>        {/* Stats */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`Total: ${Array.isArray(logs) ? logs.length : 0}`} 
            size="small" 
            color="default"
          />
          <Chip 
            label={`Showing: ${Array.isArray(filteredLogs) ? filteredLogs.length : 0}`} 
            size="small" 
            color="primary"
          />
          <Chip 
            label={`Source: ${selectedLogFile === 'all' ? 'All Services' : selectedLogFile}`} 
            size="small" 
            color="secondary"
            variant="outlined"
          />
          {lastRefresh && (
            <Chip 
              label={`Last refresh: ${formatTimestamp(lastRefresh)}`} 
              size="small" 
              color="default"
              variant="outlined"
            />
          )}
        </Box>{/* Auto-refresh toggle - DISABLED */}
        {/* <Box sx={{ mt: 2 }}>
          <Button
            variant={autoRefresh ? "contained" : "outlined"}
            size="small"
            onClick={() => setAutoRefresh(!autoRefresh)}
            color="primary"
          >
            {autoRefresh ? 'Stop Auto-refresh' : 'Start Auto-refresh'}
          </Button>
        </Box> */}
      </Paper>      {/* Logs display */}
      {loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading logs...
        </Alert>
      )}      {(!Array.isArray(filteredLogs) || filteredLogs.length === 0) && !loading ? (
        <Alert severity="info">
          No logs found. {debouncedSearchTerm || levelFilter !== 'all' ? 'Try adjusting your filters.' : 'System logs will appear here.'}
        </Alert>
      ) : (        <Paper sx={{ height: 600, overflow: 'hidden' }}>
          <VirtualList
            ref={listRef}
            height={600}
            itemCount={filteredLogs.length}
            itemSize={getItemSize}
            itemData={virtualListData}
            overscanCount={5}
          >
            {LogItem}
          </VirtualList>
        </Paper>
      )}
    </Box>
  );
});

GlobalSettingLogs.displayName = 'GlobalSettingLogs';

export default GlobalSettingLogs;
